import { useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../common/TableActionsMenu';
import { NaverHourlySpentStats, NaverDailySpentStats } from '../../../types/naver.types';
import type { Dayjs } from 'dayjs';

type Granularity = 'hourly' | 'daily';

type Props = {
  data: (NaverHourlySpentStats | NaverDailySpentStats)[];
  startDate: Dayjs;
  endDate: Dayjs;
  granularity: Granularity;
};

type ProviderMetrics = {
  impressions: number;
  clicks: number;
  postbackSuccess: number;
};

type PivotRow = {
  key: string;
  date: string;
  hour?: number;
  [provider: string]: ProviderMetrics | string | number | undefined;
};

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

const formatRate = (clicks: number, postbackSuccess: number) =>
  clicks ? `${((postbackSuccess / clicks) * 100).toFixed(1)}%` : '-';

export default function NaverSpentStatsTable({ data, startDate, endDate, granularity }: Props) {
  const tableRef = useRef<HTMLTableElement>(null);

  const filename = useMemo(
    () => `naver_${granularity}_spent_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}`,
    [granularity, startDate, endDate]
  );

  const providers = useMemo(() => {
    const seen = new Set<string>();
    data.forEach((row) => seen.add(row.provider));
    return Array.from(seen).sort();
  }, [data]);

  const pivotData = useMemo<PivotRow[]>(() => {
    const map = new Map<string, PivotRow>();
    data.forEach((row) => {
      const hour = 'hour' in row ? row.hour : undefined;
      const key = hour !== undefined ? `${row.date} ${String(hour).padStart(2, '0')}` : row.date;
      let entry = map.get(key);
      if (!entry) {
        entry = { key, date: row.date, hour };
        map.set(key, entry);
      }
      const prev = (entry[row.provider] as ProviderMetrics | undefined) ?? {
        impressions: 0,
        clicks: 0,
        postbackSuccess: 0,
      };
      entry[row.provider] = {
        impressions: prev.impressions + row.impressions,
        clicks: prev.clicks + row.clicks,
        postbackSuccess: prev.postbackSuccess + row.postbackSuccess,
      };
    });
    return Array.from(map.values());
  }, [data]);

  const { sorted, sortDirection, toggleSort } = useDateSort(pivotData, (row) => row.key);

  const cellSx = { borderLeft: '1px solid #e2e8f0' };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu tableRef={tableRef} filename={filename} />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table ref={tableRef} size="small" sx={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            {/* 1행: 날짜/시간 + 공급사명 (colspan) */}
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell rowSpan={2} sx={{ verticalAlign: 'middle', fontWeight: 700 }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  {granularity === 'hourly' ? '날짜/시간' : '날짜'}
                </TableSortLabel>
              </TableCell>
              {providers.map((p) => (
                <TableCell key={p} colSpan={4} align="center" sx={{ ...cellSx, fontWeight: 700 }}>
                  {p}
                </TableCell>
              ))}
            </TableRow>
            {/* 2행: 지표명 반복 */}
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              {providers.map((p) => (
                <>
                  <TableCell key={`${p}-impressions`} align="right" sx={cellSx}>
                    노출수
                  </TableCell>
                  <TableCell key={`${p}-clicks`} align="right">
                    클릭수
                  </TableCell>
                  <TableCell key={`${p}-postbackSuccess`} align="right">
                    포스트백 성공
                  </TableCell>
                  <TableCell key={`${p}-cvr`} align="right">
                    CVR
                  </TableCell>
                </>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.key} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell>
                  {row.hour === undefined
                    ? row.date
                    : `${row.date} ${String(row.hour).padStart(2, '0')}시-${row.hour === 23 ? 24 : row.hour + 1}시`}
                </TableCell>
                {providers.map((p) => {
                  const metrics = row[p] as ProviderMetrics | undefined;
                  return (
                    <>
                      <TableCell key={`${p}-impressions`} align="right" sx={cellSx}>
                        {metrics ? formatNumber(metrics.impressions) : '-'}
                      </TableCell>
                      <TableCell key={`${p}-clicks`} align="right">
                        {metrics ? formatNumber(metrics.clicks) : '-'}
                      </TableCell>
                      <TableCell key={`${p}-postbackSuccess`} align="right">
                        {metrics ? formatNumber(metrics.postbackSuccess) : '-'}
                      </TableCell>
                      <TableCell key={`${p}-cvr`} align="right" sx={{ fontWeight: 700 }}>
                        {metrics ? formatRate(metrics.clicks, metrics.postbackSuccess) : '-'}
                      </TableCell>
                    </>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
